import type { ComponentType } from 'react'
import { motion } from 'framer-motion'
import { Monitor, Smartphone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useModal } from '@/context/ModalContext'
import { MOBILE_APPS, POS_WINDOWS_DOWNLOAD_URL, type StoreLinks } from '@/data/apps'
import { cn } from '@/lib/utils'
import { AppleIcon, GooglePlayIcon, WindowsIcon } from './StoreIcons'

const CARD = 'flex flex-col rounded-2xl border border-dark-border bg-dark-card p-6'

function StoreBadge({
  href,
  icon: Icon,
  caption,
  store,
}: {
  href: string | null
  icon: ComponentType<{ className?: string }>
  caption: string
  store: string
}) {
  const { t } = useTranslation()
  const body = (
    <>
      <Icon className="h-5 w-5 shrink-0" />
      <span className="flex flex-col leading-none">
        <span className="text-[10px] text-white/60">{href ? caption : t('apps.soon')}</span>
        <span className="mt-0.5 text-[14px] font-semibold">{store}</span>
      </span>
    </>
  )
  const base = 'flex min-h-11 flex-1 items-center gap-2 rounded-xl border px-3 py-2 text-white'

  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cn(base, 'border-white/20 bg-black hover:border-white/40')}>
      {body}
    </a>
  ) : (
    <span aria-disabled="true" className={cn(base, 'cursor-not-allowed border-white/10 bg-black/40 opacity-50')}>
      {body}
    </span>
  )
}

function MobileAppCard({ titleKey, descKey, links }: { titleKey: string; descKey: string; links: StoreLinks }) {
  const { t } = useTranslation()
  return (
    <div className={CARD}>
      <Smartphone className="h-6 w-6 text-primary" />
      <h3 className="mt-4 text-[18px] font-semibold text-white">{t(titleKey)}</h3>
      <p className="mt-1.5 flex-1 text-[14px] leading-relaxed text-white/50">{t(descKey)}</p>
      <div className="mt-5 flex gap-2">
        <StoreBadge href={links.appStore} icon={AppleIcon} caption={t('apps.downloadOn')} store="App Store" />
        <StoreBadge href={links.googlePlay} icon={GooglePlayIcon} caption={t('apps.getItOn')} store="Google Play" />
      </div>
    </div>
  )
}

export function Apps() {
  const { t } = useTranslation()
  const { openInstall } = useModal()

  return (
    <section id="apps" className="relative bg-dark-bg py-[80px]">
      <div className="mx-auto max-w-[1200px] px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-[32px] sm:text-[40px] font-bold leading-[1.15] tracking-[-0.02em] text-white">
            {t('apps.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-[16px] leading-[1.7] text-white/55">{t('apps.subtitle')}</p>
        </motion.div>

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          <div className={CARD}>
            <Monitor className="h-6 w-6 text-primary" />
            <h3 className="mt-4 text-[18px] font-semibold text-white">{t('apps.pos.title')}</h3>
            <p className="mt-1.5 flex-1 text-[14px] leading-relaxed text-white/50">{t('apps.pos.desc')}</p>
            {/* No preventDefault: the browser starts the download and the guide opens on top. */}
            <a
              href={POS_WINDOWS_DOWNLOAD_URL}
              download
              onClick={() => openInstall('click')}
              className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-[15px] font-semibold text-white transition-colors hover:bg-primary/90"
            >
              <WindowsIcon className="h-4 w-4" />
              {t('apps.pos.button')}
            </a>
            <p className="mt-2 text-center text-[12px] text-white/40 sm:hidden">{t('apps.pos.mobileHint')}</p>
          </div>

          <MobileAppCard titleKey="apps.owner.title" descKey="apps.owner.desc" links={MOBILE_APPS.owner} />
          <MobileAppCard titleKey="apps.waiter.title" descKey="apps.waiter.desc" links={MOBILE_APPS.waiter} />
        </div>
      </div>
    </section>
  )
}
